import { useState } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import useCustomRouter from '@/hooks/useCustomRouter';
import useAccount from '@/lib/hooks/useAccount';
import useBusinessUnits from '@/lib/hooks/useBusinessUnits';
import useCart from '@/lib/hooks/useCart';
import { useCategories } from '@/lib/hooks/useCategories';
import useStores from '@/lib/hooks/useStores';
import { mapCategory } from '@/utils/mappers/map-category';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import { useDebounce } from '@/hooks/useDebounce';
import useProductSearch from '@/lib/hooks/useProductSearch';
import { mapCsvProduct } from '@/utils/mappers/map-csv-product';
import { mapProductSuggestion } from '@/utils/mappers/map-product-suggestion';
import { ProductSuggestion } from '@/components/organisms/search/types';
import { ResolvedSku } from '@/components/organisms/quick-order-upload/types';
import { sdk } from '@/sdk';
import { Product } from '@shared/types/product';
import { resolveStockStatus } from '@/utils/product/resolve-stock-status';
import {
  parseCtLifecycleCodes,
  isStockDueSoonEligibleFromCodes,
  isRemovedFromSaleForLocale,
} from '@/utils/product/lifecycle';
import useSwrClearCache from '@/hooks/useSwrClearCache';

const useHeaderData = () => {
  const router = useCustomRouter();

  const { locale } = useParams();

  const clearCache = useSwrClearCache();

  const searchParams = useSearchParams();
  const searchQuery = searchParams.get('query');

  const { defaultBusinessUnit } = useBusinessUnits();

  const { stores } = useStores();

  const mappedStores = (stores ?? []).map((st) => {
    return { name: st.name ?? st.key, value: st.key };
  });

  const { selectedBusinessUnit, selectedStore } = useStoreAndBusinessUnits();

  // EWM-441 — the badge shows distinct line items, not the summed quantity.
  // EWM-247 — the Quick Order drawers add via the dedicated `quickOrder/addItems`
  // BFF action (partial-success + `failed[]`), not the general single-item add.
  // EWM-942 — `quickOrderUploadFile` parses an uploaded .xls/.xlsx server-side
  // for the Order Upload drawer. Read-only; the cart add still goes through
  // `quickOrderAddItems`.
  const {
    totalLineItems: totalCartItems,
    quickOrderAddItems,
    quickOrderUploadFile,
  } = useCart(selectedBusinessUnit?.key, selectedStore?.key);

  const { categories } = useCategories();

  const navigationCategories = categories
    .map((c) => mapCategory(c, { locale }))
    .filter((category) => !!category.name || !!category.path);

  // EWM-644 — removed the header's useQuotes. Quotes are external (CT quote store is
  // bypassed), so quote/query + queryQuoteRequests always returned empty here, and the
  // mapped "pending quotes" value was never consumed by the header. Two dead calls/load
  // removed. The quote surfaces (quotes tab, quote-detail, …) still call useQuotes themselves.

  const { account, logout } = useAccount();

  const onLogoutClick = () => {
    logout().then(() => {
      // EWM-651 — /login is an unpublished route (404); logout returns the user
      // to the published home.
      router.push('/');
      router.refresh();
      clearCache();
    });
  };

  const [quickOrderSearch, setQuickOrderSearch] = useState('');

  const debouncedQuickOrderSearch = useDebounce<string>(quickOrderSearch, 150);

  const onQuickOrderSearch = (value: string) => setQuickOrderSearch(value);

  const { products: quickOrderProducts } = useProductSearch(
    debouncedQuickOrderSearch,
    undefined,
    undefined,
    selectedStore?.key,
  );

  const [headerSearch, setHeaderSearch] = useState(searchQuery || '');

  const debouncedHeaderSearch = useDebounce<string>(headerSearch, 150);

  const onHeaderSearch = (value: string) => setHeaderSearch(value);

  const { products: headerProducts } = useProductSearch(
    debouncedHeaderSearch,
    undefined,
    undefined,
    selectedStore?.key,
  );

  const headerSearchAction = () => router.push(`/search/?query=${headerSearch}`);

  const [skus, setSKUs] = useState<string[] | undefined>([]);

  const handleSKUsUpdate = (skus: string[]) => setSKUs(skus);

  const { products: csvShowProducts, isLoading: csvShowProductsLoading } = useProductSearch(
    '',
    skus,
    skus?.length ?? 0,
    selectedStore?.key,
  );

  const skuLookup = async (sku: string) => {
    const result = await sdk.composableCommerce.product.query({ skus: [sku], limit: 1, storeKey: selectedStore?.key });
    if (result.isError || !result.data.items?.length) return null;
    return mapProductSuggestion(result.data.items[0] as Product);
  };

  // EWM-605 — free-text catalogue search used by the Order Upload drawer to
  // suggest the correct product for an unmatched/typo'd SKU, so a partial
  // upload can be fixed and added manually instead of silently dropped.
  const searchProducts = async (query: string): Promise<ProductSuggestion[]> => {
    const trimmed = query.trim();
    if (!trimmed) return [];
    const result = await sdk.composableCommerce.product.query({
      query: trimmed,
      limit: 5,
      storeKey: selectedStore?.key,
    });
    if (result.isError || !result.data.items?.length) return [];
    return (result.data.items as Product[]).map(mapProductSuggestion);
  };

  // EWM-605 — batch-resolve uploaded Order Upload codes against the catalogue.
  // Returns the subset of `codes` that exist (matched case-insensitively across
  // each product's variant SKUs). The upload drawer marks the rest as failed.
  //
  // EWM-645 — each resolved SKU carries its stock status, derived from the same
  // CT `isOnStock` + `productLifeCycle` source the PDP/PLP/cart use (via
  // resolveStockStatus), so the upload preview badges rows and keeps
  // out-of-stock / due-soon rows out of the cart.
  //
  // commercetools Product Search caps `limit` at 100, so a bulk file is chunked
  // into ≤100-code queries (otherwise a >100-row upload would error and wrongly
  // flag every row as not-found). A query error THROWS so the caller falls back
  // to optimistic-valid rather than failing the whole batch on a transient blip;
  // a successful query that simply finds nothing returns [] (those rows are
  // genuinely not found).
  const resolveSkus = async (codes: string[]): Promise<ResolvedSku[]> => {
    if (codes.length === 0) return [];
    const CT_SEARCH_LIMIT = 100;
    const found = new Map<string, ResolvedSku>();
    for (let i = 0; i < codes.length; i += CT_SEARCH_LIMIT) {
      const chunk = codes.slice(i, i + CT_SEARCH_LIMIT);
      const result = await sdk.composableCommerce.product.query({
        skus: chunk,
        limit: chunk.length,
        storeKey: selectedStore?.key,
      });
      if (result.isError) throw new Error('resolveSkus: product query failed');
      for (const product of (result.data.items as Product[]) ?? []) {
        for (const variant of product.variants ?? []) {
          if (!variant.sku) continue;
          const lifecycleCodes = parseCtLifecycleCodes((variant.attributes ?? {}) as Record<string, unknown>);
          const isStockDueSoonEligible = isStockDueSoonEligibleFromCodes(lifecycleCodes);
          const stockStatus = resolveStockStatus({ inStock: variant.isOnStock, isStockDueSoonEligible });
          found.set(variant.sku.toLowerCase(), {
            sku: variant.sku,
            stockStatus,
            // EWM-944 — the back-order inputs, so the upload drawer can apply the
            // EWM-360 rules per row rather than rejecting every non-in-stock row.
            inStock: variant.isOnStock,
            availableQuantity: variant.availableQuantity,
            // Region-scoped (EWM-360/EWM-643): GO is per-country, and the
            // aggregated `lifecycleCodes` above are intentionally cross-company
            // for badges only. Deriving the GO gate from them would reject an
            // NZ-only GO SKU on the AU uploader and disagree with the BFF.
            removedFromSale: isRemovedFromSaleForLocale(
              (variant.attributes ?? {}) as Record<string, unknown>,
              typeof locale === 'string' ? locale : undefined,
            ),
          });
        }
      }
    }
    // Preserve the uploaded order and original casing for matched codes.
    return codes.map((code) => found.get(code.toLowerCase())).filter((r): r is ResolvedSku => !!r);
  };

  return {
    account,
    navigationCategories,
    defaultBusinessUnit,
    selectedStore: selectedStore?.key,
    stores: mappedStores,
    selectedBusinessUnit: selectedBusinessUnit?.key,
    // EWM-944 — back-order entitlement, read from the ACTIVE BU's own custom
    // field. Parent inheritance is UNVERIFIED: nothing in the BFF walks
    // `parentUnit` (BusinessUnitMapper reads `back-orders-allowed` off the BU
    // itself and fails closed when absent), so a child BU missing the flag is
    // silently restricted even if its parent is entitled. Tracked separately.
    backOrdersAllowed: selectedBusinessUnit?.backOrdersAllowed,
    // EWM-944 — needed alongside `backOrdersAllowed` because a cash account
    // (`payment-terms` = "P") cannot back-order even when the BU allows it.
    paymentTerms: selectedBusinessUnit?.paymentTerms,
    totalCartItems,
    headerSearch,
    headerProducts,
    quickOrderSearch,
    quickOrderProducts,
    csvShowProducts: csvShowProducts
      .map((product) =>
        product.variants.map((variant) => mapCsvProduct(variant, typeof locale === 'string' ? locale : undefined)),
      )
      .flat(),
    csvShowProductsLoading,
    addToCart: quickOrderAddItems,
    // EWM-942 — server-side .xls/.xlsx parse for the Order Upload drawer.
    parseUploadFile: quickOrderUploadFile,
    onHeaderSearch,
    onQuickOrderSearch,
    onLogoutClick,
    headerSearchAction,
    handleSKUsUpdate,
    skuLookup,
    resolveSkus,
    searchProducts,
  };
};
export default useHeaderData;
